function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef } from 'react';
import { useInputState } from 'theming/hooks/useInputState';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { useFormContext } from 'components/Form/FormContext';
import { InputBase } from 'components/InputBase/InputBase';
import { determineComponentState } from 'components/InputBase/InputBase.utils';
import { InputCharacterCounter } from 'components/InputCharacterCounter/InputCharacterCounter';
import { InputErrorMessage } from 'components/InputErrorMessage/InputErrorMessage';
import { InputLabel } from 'components/InputLabel/InputLabel';
import { Tooltip } from 'components/Tooltip/Tooltip';
import * as S from './InputControl.styles';
const InputControl = /*#__PURE__*/React.forwardRef(({
  ariaLabel,
  label,
  id,
  disabled,
  required,
  optional,
  readOnly,
  placeholder,
  hasError,
  errorMessage,
  width = '100%',
  size,
  orientation,
  startAdornment,
  endAdornment,
  maxLength,
  currentLength,
  showCharacterCounter = true,
  tooltipText,
  tooltipProps = {},
  leftContainerWidth,
  forceLeftAlignment,
  ...baseProps
}, ref) => {
  if (required && optional) {
    console.warn('An input can be required or optional but not both.');
  }
  const internalRef = useRef(null);
  const context = useFormContext();
  const {
    isFocused,
    isHovered
  } = useInputState(internalRef);
  const inputState = determineComponentState({
    error: hasError,
    disabled,
    readOnly,
    isFocused,
    isHovered
  });
  const isInteractive = !(disabled || readOnly);
  const hasErrorMessage = inputState === 'error' && !!errorMessage;
  const hasCharacterCounter = !!(showCharacterCounter && maxLength);
  const showFooter = hasErrorMessage || hasCharacterCounter;
  const handleContainerClick = () => {
    if (isInteractive && !isFocused) {
      internalRef.current?.focus();
    }
  };
  const inputProps = {
    id,
    ariaLabel,
    disabled,
    readOnly,
    required,
    placeholder,
    maxLength,
    forceLeftAlignment,
    ...baseProps
  };
  const assignSharedRefs = createAssignSharedRefs(ref, internalRef);
  const getIsHorizontal = () => {
    if (orientation === 'vertical') {
      return false;
    }
    return orientation === 'horizontal' || context?.orientation === 'horizontal';
  };
  const isHorizontal = getIsHorizontal();
  const getSize = () => {
    if (size) {
      return size;
    }
    if (context?.inputSize) {
      return context.inputSize;
    }
    if (isHorizontal) {
      return 'medium';
    }
    return 'large';
  };
  const sizeVariant = getSize();
  return /*#__PURE__*/React.createElement(S.InputControlWrapper, {
    onClick: handleContainerClick,
    $isInteractive: isInteractive,
    $isHorizontal: isHorizontal,
    $marginLeft: leftContainerWidth
  }, /*#__PURE__*/React.createElement(S.InputHeaderContainer, {
    $isHorizontal: isHorizontal
  }, /*#__PURE__*/React.createElement(S.InputHeaderLeftContainer, {
    $width: leftContainerWidth,
    $isHorizontal: isHorizontal
  }, !!label ? /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    inputState: inputState,
    optional: optional,
    required: required,
    orientation: orientation ?? context?.orientation
  }, label) : null, !!tooltipText ? /*#__PURE__*/React.createElement(Tooltip, _extends({
    content: tooltipText
  }, tooltipProps)) : null)), /*#__PURE__*/React.createElement(S.InputContainerWrapper, {
    $width: width,
    $isHorizontal: isHorizontal
  }, /*#__PURE__*/React.createElement(S.InputContainer, {
    $state: inputState,
    $size: sizeVariant,
    $isInteractive: isInteractive,
    $isHorizontal: isHorizontal
  }, startAdornment ? /*#__PURE__*/React.createElement(S.AdornmentWrapper, {
    $size: sizeVariant,
    $state: inputState,
    "aria-hidden": true
  }, startAdornment) : null, /*#__PURE__*/React.createElement(InputBase, _extends({
    ref: assignSharedRefs,
    inputState: inputState,
    size: sizeVariant
  }, inputProps)), endAdornment ? /*#__PURE__*/React.createElement(S.AdornmentWrapper, {
    $size: sizeVariant,
    $state: inputState,
    "aria-hidden": true
  }, endAdornment) : null), showFooter ? /*#__PURE__*/React.createElement(S.InputFooterContainer, null, hasErrorMessage ? /*#__PURE__*/React.createElement(InputErrorMessage, {
    orientation: orientation ?? context?.orientation
  }, errorMessage) : null, hasCharacterCounter ? /*#__PURE__*/React.createElement(InputCharacterCounter, {
    currentLength: currentLength ?? 0,
    inputState: inputState,
    maxLength: maxLength
  }) : null) : null));
});
export { InputControl };